import { RealtimeEvents } from '@raahi/shared';
import { driverDemoState } from './store.js';

type Handler = (payload: unknown) => void;

interface DemoSocketShape {
  on: (event: string, handler: Handler) => DemoSocketShape;
  off: (event: string, handler?: Handler) => DemoSocketShape;
  emit: (event: string, payload?: unknown) => DemoSocketShape;
  disconnect: () => void;
}

/** Mirrors the rider demo socket but also wires the driver app's outbound events back into the store. */
export function createDemoSocket(): DemoSocketShape {
  const handlers = new Map<string, Set<Handler>>();
  const unsubs: Array<() => void> = [];

  for (const event of ['trip:offer', 'trip:update', 'trip:fare', 'driver:location']) {
    unsubs.push(
      driverDemoState.on(event, (payload) => {
        handlers.get(event)?.forEach((fn) => fn(payload));
      }),
    );
  }

  const socket: DemoSocketShape = {
    on(event, handler) {
      let set = handlers.get(event);
      if (!set) {
        set = new Set();
        handlers.set(event, set);
      }
      set.add(handler);
      return socket;
    },
    off(event, handler) {
      if (!handler) handlers.delete(event);
      else handlers.get(event)?.delete(handler);
      return socket;
    },
    emit(event, payload) {
      const tripId = (payload as { tripId?: string } | undefined)?.tripId;
      switch (event) {
        case RealtimeEvents.TripAccept:
          if (tripId) driverDemoState.acceptOffer(tripId);
          break;
        case RealtimeEvents.TripDecline:
          driverDemoState.declineOffer();
          break;
        case RealtimeEvents.TripArrived:
          driverDemoState.markArrived();
          break;
        case RealtimeEvents.TripStart:
          driverDemoState.startTrip();
          break;
        case RealtimeEvents.TripComplete:
          driverDemoState.completeTrip();
          break;
        default:
          break;
      }
      return socket;
    },
    disconnect() {
      handlers.clear();
      for (const u of unsubs) u();
    },
  };
  return socket;
}
